import { api } from "../../api/client";
import type { NamedPlace, PlaceHit, PlaceSearchResult } from "../../api/types";
import { k, lang, locale } from "../../i18n/i18n";
import { DEBOUNCE_MS, MAX_RESULTS, parseQuery } from "./placeSearch";

/** Suggestions du champ « Lieu du nouveau terrain » des Données initiales (chantier « Données initiales balisées +
 *  lieu nommé », 31/10/2026) : aucun appel extérieur. Quatre groupes, dans l'ordre d'affichage :
 *  - « Mes terrains » : les terrains libres de la licence dont le nom correspond (les choisir reprend le terrain) ;
 *  - « Espaces d'entraînement » : les camps dont le nom correspond (les choisir passe sur ce camp) ;
 *  - « Lieux » : les localités du répertoire hors ligne du serveur (`POST /places/search`, saisie dans le corps) ;
 *  - « Coordonnées » : coordonnées décimales ou MGRS reconnus sur le poste (`parseQuery`), sans requête.
 *  Trois caractères au moins, requête partie 300 ms après la dernière frappe, la précédente annulée, réponses gardées
 *  pour la session (mémoire du module : un champ remonté ne redemande rien). Le pays de la force (`country`) départage
 *  les homonymes (« Brest » de France avant celui de Biélorussie pour une force française). */

export type SuggestGroup = "terrains" | "camps" | "places" | "coords";

export const SUGGEST_LABELS: Readonly<Record<SuggestGroup, string>> = {
  terrains: k("Mes terrains"),
  camps: k("Espaces d'entraînement"),
  places: k("Lieux"),
  coords: k("Coordonnées"),
};
const ORDER: readonly SuggestGroup[] = ["terrains", "camps", "places", "coords"];

/** Limites du terrain libre (celles du serveur, `NamedPlace`) : au-delà, aucun terrain ne se prépare. */
export const LAT_MIN = -79.5;
export const LAT_MAX = 83.5;

/** Carte de la licence proposée par le champ : terrain libre déjà préparé, ou camp. */
export interface KnownMap {
  slug: string;
  name: string;
}

export type Suggestion =
  | { group: "terrains" | "camps"; id: string; name: string; detail?: string; slug: string }
  | {
      group: "places" | "coords";
      id: string;
      name: string;
      detail?: string;
      place: NamedPlace;
      /** Lettres OTAN de l'heure légale au lieu (localité du répertoire seulement). */
      zones?: { winter: string; summer: string };
    };

export type SuggestState =
  | { status: "idle" }
  | { status: "short" }
  | { status: "outside" }
  | { status: "loading"; text: string; local: Suggestion[] }
  | { status: "done"; text: string; items: Suggestion[] }
  | { status: "unavailable"; text: string; local: Suggestion[] };

/** Nom replié pour la comparaison : minuscules, sans accents ni signes (« Ādaži » → « adazi »). */
export function fold(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLocaleLowerCase("fr")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

/** Cartes de la licence dont le nom contient la saisie (mot entier ou début de mot), début du nom d'abord. */
export function matchMaps(maps: readonly KnownMap[], text: string, group: "terrains" | "camps"): Suggestion[] {
  const needle = fold(text);
  if (!needle) return [];
  const scored = maps
    .map((m) => ({ m, name: fold(m.name) }))
    .filter(({ name }) => name.startsWith(needle) || name.includes(` ${needle}`))
    .sort((a, b) => Number(b.name.startsWith(needle)) - Number(a.name.startsWith(needle)) || a.name.localeCompare(b.name));
  return scored.slice(0, 3).map(({ m }) => ({ group, id: `${group}:${m.slug}`, name: m.name, slug: m.slug }));
}

/** Nom du pays d'un code ISO, dans la langue de l'interface (le code lui-même à défaut). */
export function countryName(code: string): string {
  try {
    return new Intl.DisplayNames([locale()], { type: "region" }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/** Lieu nommé d'une localité du répertoire : « Tartu », seconde ligne « Tartumaa, Estonie ». */
export function hitPlace(hit: PlaceHit): NamedPlace {
  const country = countryName(hit.country);
  const region = [hit.region, country].filter((part): part is string => Boolean(part && part.trim())).join(", ");
  return { name: hit.name, region: region || null, lon: hit.lon, lat: hit.lat, source: "place", ref: hit.id };
}

function hitSuggestion(hit: PlaceHit): Suggestion {
  const place = hitPlace(hit);
  return {
    group: "places",
    id: hit.id,
    name: hit.name,
    detail: place.region ?? undefined,
    place,
    zones: { winter: hit.time_zone_winter, summer: hit.time_zone_summer },
  };
}

/** « 58,38° N 26,72° E » (« O » pour l'ouest en français, « W » en anglais). */
export function formatLatLon(lat: number, lon: number): string {
  const n = (v: number) => Math.abs(v).toLocaleString(locale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const west = lang() === "en" ? "W" : "O";
  return `${n(lat)}° ${lat < 0 ? "S" : "N"} ${n(lon)}° ${lon < 0 ? west : "E"}`;
}

/** Suggestions rangées par groupe, dans l'ordre d'affichage. */
export function groupSuggestions(items: readonly Suggestion[]): { group: SuggestGroup; items: Suggestion[] }[] {
  return ORDER.map((group) => ({ group, items: items.filter((s) => s.group === group) })).filter((g) => g.items.length > 0);
}

/** Réponses du répertoire gardées pour la session (tous les champs du poste). */
const memory = new Map<string, PlaceSearchResult>();

/** Oubli de la mémoire de session (essais). */
export function forgetPlaceSearches(): void {
  memory.clear();
}

type Search = (body: { text: string; limit: number; country?: string }, signal?: AbortSignal) => Promise<PlaceSearchResult>;

/** Moteur du champ : délai, annulation, mémoire de session ; `search` et le délai sont injectés pour les essais.
 *  `onState` reçoit chaque état ; les cartes de la licence sont lues à chaque saisie (`maps`). */
export class PlaceSuggester {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private controller: AbortController | null = null;
  private seq = 0;

  constructor(
    private readonly options: {
      onState: (state: SuggestState) => void;
      maps: () => { terrains: readonly KnownMap[]; camps: readonly KnownMap[] };
      country?: () => string | undefined;
      search?: Search;
      delay?: number;
    },
  ) {}

  /** Nouvelle saisie : coordonnées et MGRS répondent aussitôt ; un nom montre tout de suite les cartes de la licence
   *  qui lui correspondent, puis les localités du répertoire après le délai. */
  input(raw: string): void {
    this.cancel();
    const text = raw.trim().replace(/\s+/g, " ");
    if (!text) return this.options.onState({ status: "idle" });
    const parsed = parseQuery(text);
    if (parsed.kind === "point") {
      const [lon, lat] = parsed.result.center;
      if (lat < LAT_MIN || lat > LAT_MAX) return this.options.onState({ status: "outside" });
      // Coordonnées : nommées comme le poste les écrit (« 58,38° N 26,72° E ») ; MGRS : tel que saisi, la position dessous.
      const mgrs = parsed.source === "mgrs";
      const name = mgrs ? parsed.result.name : formatLatLon(lat, lon);
      const place: NamedPlace = { name, region: null, lon, lat, source: parsed.source, ref: text.slice(0, 40) };
      return this.options.onState({
        status: "done",
        text,
        items: [{ group: "coords", id: parsed.result.id, name, detail: mgrs ? formatLatLon(lat, lon) : undefined, place }],
      });
    }
    if (parsed.kind === "short") return this.options.onState({ status: "short" });
    const { terrains, camps } = this.options.maps();
    const local = [...matchMaps(terrains, parsed.text, "terrains"), ...matchMaps(camps, parsed.text, "camps")];
    const country = this.options.country?.();
    const key = `${country ?? ""}|${fold(parsed.text)}`;
    const cached = memory.get(key);
    if (cached) return this.options.onState(this.answer(parsed.text, local, cached));
    this.options.onState({ status: "loading", text: parsed.text, local });
    const seq = ++this.seq;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.run(parsed.text, country, key, local, seq);
    }, this.options.delay ?? DEBOUNCE_MS);
  }

  private answer(text: string, local: Suggestion[], result: PlaceSearchResult): SuggestState {
    if (!result.available) return { status: "unavailable", text, local };
    return { status: "done", text, items: [...local, ...result.places.slice(0, MAX_RESULTS).map(hitSuggestion)] };
  }

  private async run(text: string, country: string | undefined, key: string, local: Suggestion[], seq: number): Promise<void> {
    const controller = new AbortController();
    this.controller = controller;
    try {
      const search = this.options.search ?? api.searchPlaces;
      const result = await search({ text, limit: MAX_RESULTS, ...(country ? { country } : {}) }, controller.signal);
      memory.set(key, result);
      if (seq === this.seq) this.options.onState(this.answer(text, local, result));
    } catch (error) {
      if ((error as { name?: string })?.name === "AbortError" || controller.signal.aborted) return;
      // Serveur injoignable : rien n'est gardé, la saisie suivante redemande ; coordonnées et MGRS restent possibles.
      if (seq === this.seq) this.options.onState({ status: "unavailable", text, local });
    } finally {
      if (this.controller === controller) this.controller = null;
    }
  }

  /** Saisie effacée ou remplacée : délai en cours abandonné, requête en vol annulée. */
  cancel(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.controller?.abort();
    this.controller = null;
    this.seq++;
  }
}
